import { useState, useEffect, useRef } from "react";
import { DonwloadIcon1, LoadingIcon } from "../../assets/icons";
import { getNonFinancialUploadLog, getNonFinancialReasons } from "../../service/query/auth";
import FilterDropdown from "./FilterDropdown";
import DateRangeFilter from "./DateRangeFilter";

const statusStyles = {
  PENDING: "bg-[#F3F4F6] text-[#6B7280]",
  PROCESSING: "bg-[#EDF6FF] text-[#0057FF]",
  SUCCESSFUL: "bg-[#E7F7EF] text-[#0E9F6E]",
  FAILED: "bg-[#FDECEC] text-[#E02424]",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-[10px] py-[3px] rounded-full text-[12px] font-medium ${statusStyles[status] ?? "bg-[#F3F4F6] text-[#6B7280]"}`}>
    <span className="w-[6px] h-[6px] rounded-full bg-current" />
    {status}
  </span>
);

export default function PointsAwardReports() {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reason, setReason] = useState("");
  const [reasons, setReasons] = useState([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [uploads, setUploads] = useState([]);


  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const res = await getNonFinancialReasons();
        setReasons(res?.data?.data ?? []);
      } catch (err) {
        console.error("Failed to fetch reasons:", err);
      }
    };
    fetchReasons();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await getNonFinancialUploadLog({
        membershipId: search || undefined,
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        reason: reason || undefined,
        page,
        perPage: 50,
      });
      setReportData(res?.data?.data ?? []);
      setPagination(res?.data?.pagination ?? null);
      setUploads(res?.data?.uploads ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExportCSV = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const res = await getNonFinancialUploadLog({
        membershipId: search || undefined,
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        reason: reason || undefined,
        paginate: false,
      });

      const allRecords = res?.data?.data ?? [];

      if (!allRecords.length) return;

      const flatRows = allRecords.map((r) => ({
        bill_no: r.bill_no ?? "",
        membership_id: r.membership_id ?? "",
        points: r.points ?? "",
        amount: r.amount ?? "",
        transaction_date: r.transaction_date ?? "",
        transaction_type: r.transaction_type ?? "",
        reason: r.reason ?? "",
        transaction_id: r.transaction_id ?? "",
        source_account: r.source_account ?? "",
        transaction_channel: r.transaction_channel ?? "",
        processing_status: r.processing_status ?? "",
        response_code: r.response_code ?? "",
        response_message: r.response_message ?? "",
        uploaded_by: r.uploaded_by?.name ?? "",
        processed_at: r.processed_at ?? "",
      }));

      const headers = Object.keys(flatRows[0]).join(",");
      const csvRows = flatRows.map((row) =>
        Object.values(row).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
      ).join("\n");

      const csv = `${headers}\n${csvRows}`;
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "points-award-report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="pt-6 bg-white min-h-[80vh]">
      <h3 className="text-[16px] font-semibold text-[#212121] mb-[24px]">
        Non-Financial Point Award Reports
      </h3>

      <div className="border border-[#EAECF0] rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 border border-[#D0D5DD] rounded-full px-4 py-2 w-[280px]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="#98A2B3" strokeWidth="1.5" />
              <path d="M21 21L16.65 16.65" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search membership ID"
              className="text-sm outline-none w-full text-[#344054] placeholder:text-[#98A2B3]"
            />
          </div>

          <FilterDropdown
            label="Reason"
            value={reason}
            onChange={setReason}
            options={[
              { value: "", label: "All" },
              ...reasons.map((r) => ({ value: r, label: r })),
            ]}
          />

          <FilterDropdown
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: "", label: "All" },
              { value: "pending", label: "Pending" },
              { value: "processing", label: "Processing" },
              { value: "successful", label: "Successful" },
              { value: "failed", label: "Failed" },
              { value: "unprocessed", label: "Unprocessed" },
            ]}
          />

          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom, dateTo }) => {
              setDateFrom(dateFrom);
              setDateTo(dateTo);
            }}
          />

          <button
            onClick={() => { setPage(1); fetchReport(); }}
            className="border rounded px-4 py-2 text-sm"
          >
            Show results
          </button>
          <button
            onClick={() => {
              setSearch("");
              setStatus("");
              setReason("");
              setDateFrom("");
              setDateTo("");
            }}
            className="text-sm flex items-center gap-1 text-[#344054]"
          >
            ✕ Clear filters
          </button>

          <div className="ml-auto relative" ref={exportRef}>
            <button
              onClick={() => setShowExportMenu((v) => !v)}
              disabled={exporting}
              className="border border-[#D0D5DD] rounded px-4 py-2 flex items-center gap-2 disabled:opacity-50"
            >
              {exporting ? <LoadingIcon /> : <DonwloadIcon1 />}
              {exporting ? "Exporting..." : "Export List"}
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-[160px] bg-white border border-[#EAECF0] rounded-lg shadow-lg z-10">
                <p className="px-4 pt-3 pb-1 text-[12px] font-semibold text-[#101828]">Export as</p>
                <button
                  onClick={handleExportCSV}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                >
                  CSV
                </button>
                <button disabled className="w-full text-left px-4 py-2 text-sm text-gray-300 cursor-not-allowed">PDF</button>
                <button disabled className="w-full text-left px-4 py-2 text-sm text-gray-300 cursor-not-allowed">Excel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingIcon />
        </div>
      ) : (
        <div className="rounded-md border border-[#EAECF0] overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
            <table className="w-full min-w-[1600px]">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  {[
                    "Bill Number", "Membership ID", "Points", "Amount",
                    "Transaction date", "Transaction type", "Reason",
                    "Transaction ID", "Source account", "Transaction channel",
                    "Processing Status", "Response code", "Response message",
                    "Uploaded by", "Processed at", "Processing Triggered",
                  ].map((item) => (
                    <th key={item} className="px-6 py-4 text-left text-xs font-semibold whitespace-nowrap border-b">
                      {item}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center py-10 text-gray-400">
                      No report data available
                    </td>
                  </tr>
                ) : (
                  reportData.map((row) => (
                    <tr key={row.id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">{row.bill_no ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.membership_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.points}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.amount}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.transaction_date}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.transaction_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.reason ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.transaction_id ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.source_account ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.transaction_channel ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={row.processing_status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.response_code ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.response_message ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.uploaded_by?.name ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{row.processed_at ?? "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {row.upload?.processing_trigger
                          ? <span className="text-[#0E9F6E] font-medium">Yes</span>
                          : <span className="text-[#E02424] font-medium">No</span>
                        }
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && (
            <div className="flex items-center justify-between mt-4 px-4 pb-4">
              <p className="text-[12px] text-[#64748B]">
                Page {pagination.current_page} of {pagination.last_page} — {pagination.total} records
              </p>
              {pagination.last_page > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.current_page === 1}
                    className={`px-3 py-1 rounded text-[12px] border transition-all ${pagination.current_page === 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-[#00305E] text-[#00305E] hover:bg-blue-50"}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                    disabled={pagination.current_page === pagination.last_page}
                    className={`px-3 py-1 rounded text-[12px] border transition-all ${pagination.current_page === pagination.last_page ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-[#00305E] text-[#00305E] hover:bg-blue-50"}`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}